import React from 'react';

import { Formik } from 'formik';
import DatePicker from 'react-datepicker';
import * as Yup from 'yup';

import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextFormField from 'CommonComponents/TextFormField';
import Tooltip from 'CommonComponents/Tooltip';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';

export default function PayrateModal(props) {
    const validationSchema = Yup.object().shape({
        name: Yup.string().required($t('This value is required')),
        min: Yup.number().min(0, $t('Must be greater than or equal to 0')).required($t('This value is required')),
        max: Yup.number().min(Yup.ref('min'), $t('greater than or equal to min')).required($t('This value is required')),
        inflation: Yup.object().shape(
            Object.fromEntries(
                Object.keys(props.defaultPercentage.inflation).map((year) => [
                    year,
                    props.defaultPercentage.inflation[year] === null
                        ? Yup.mixed().nullable()
                        : Yup.number()
                              .min(0, $t('Must be greater than or equal to 0'))
                              .max(100, $t('Must be less than or equal to 100'))
                              .test(
                                  'decimal-precision',
                                  $t('Max precision is 0.1'),
                                  (value) => value === undefined || /^\d+(\.\d{1})?$/.test(value.toString()),
                              ),
                ]),
            ),
        ),
        bonus: Yup.object().shape(
            Object.fromEntries(
                Object.keys(props.defaultPercentage.bonus).map((year) => [
                    year,
                    props.defaultPercentage.bonus[year] === null
                        ? Yup.mixed().nullable()
                        : Yup.number()
                              .min(0, $t('Must be greater than or equal to 0'))
                              .max(100, $t('Must be less than or equal to 100'))
                              .test(
                                  'decimal-precision',
                                  $t('Max precision is 0.1'),
                                  (value) => value === undefined || /^\d+(\.\d{1})?$/.test(value.toString()),
                              ),
                ]),
            ),
        ),
        onCosts: Yup.object().shape(
            Object.fromEntries(
                Object.keys(props.defaultPercentage.onCosts).map((year) => [
                    year,
                    props.defaultPercentage.onCosts[year] === null
                        ? Yup.mixed().nullable()
                        : Yup.number()
                              .min(0, $t('Must be greater than or equal to 0'))
                              .max(100, $t('Must be less than or equal to 100'))
                              .test(
                                  'decimal-precision',
                                  $t('Max precision is 0.1'),
                                  (value) => value === undefined || /^\d+(\.\d{1})?$/.test(value.toString()),
                              ),
                ]),
            ),
        ),
        bonusPayDate: Yup.object().shape(
            Object.fromEntries(
                Object.keys(props.defaultPercentage.bonus).map((year, index, arr) => [
                    year,
                    Yup.date()
                        .required($t('Bonus pay date is required'))
                        .min(index === 0 ? props.eventStartDate : new Date(`${year}-01-01`), $t('Date cannot be earlier than event start date'))
                        .max(index === arr.length - 1 ? props.eventEndDate : new Date(`${year}-12-31`), $t('Date cannot be later than event end date')),
                ]),
            ),
        ),
    });

    const toast = useToast();
    const savePayrateRequest = useApiRequest({ url: props.saveUrl });

    return (
        <>
            <div id={props.modalId} ref={props.modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
                <div className="modal__container">
                    <div className="modal__inner-container">
                        <ModalHeader title={props.payrate ? $t('Edit Pay Rate') : $t('Add Pay Rate')} close={props.onClose}></ModalHeader>
                        <div className="tw-rounded-b">
                            <div className="modal__form-container">
                                <Formik
                                    initialValues={{
                                        name: props.payrate ? props.payrate.name : '',
                                        min: props.payrate ? props.payrate.min : '',
                                        max: props.payrate ? props.payrate.max : '',
                                        inflation: { ...props.defaultPercentage.inflation },
                                        bonus: { ...props.defaultPercentage.bonus },
                                        onCosts: { ...props.defaultPercentage.onCosts },
                                        bonusPayDate: Object.keys(props.defaultPercentage.bonusPayDate).reduce((acc, year) => {
                                            const storedDate = props.payrate?.percentage?.bonusPayDate?.[year];

                                            if (!storedDate) {
                                                acc[year] = null;
                                            } else {
                                                try {
                                                    const parsedDate = new Date(storedDate + 'T00:00:00Z');
                                                    acc[year] = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
                                                } catch (e) {
                                                    console.error(`Invalid date format for year ${year}:`, storedDate);
                                                    acc[year] = null;
                                                }
                                            }

                                            return acc;
                                        }, {}),
                                    }}
                                    enableReinitialize={true}
                                    validationSchema={validationSchema}
                                    onSubmit={(values, actions) => {
                                        const data = {};
                                        data.name = values.name;
                                        data.min = values.min;
                                        data.max = values.max;
                                        data.inflation = values.inflation;
                                        data.onCosts = values.onCosts;
                                        data.bonus = values.bonus;
                                        data.bonusPayDate = {};

                                        Object.keys(values.bonusPayDate).forEach((year) => {
                                            const val = values.bonusPayDate[year];
                                            if (val instanceof Date) {
                                                data.bonusPayDate[year] =
                                                    `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`;
                                            } else {
                                                data.bonusPayDate[year] = val;
                                            }
                                        });

                                        if (props.payrate) {
                                            savePayrateRequest
                                                .put(data)
                                                .then((res) => {
                                                    actions.resetForm();
                                                    props.onSuccess();
                                                    toast.success($t('Payrate was saved successfully'));
                                                })
                                                .catch((err) => {
                                                    console.log({ err });
                                                    toast.error(err.response?.data?.message || $t('Something went wrong while saving the payrate'));
                                                })
                                                .finally(() => {
                                                    actions.setSubmitting(false);
                                                });
                                        } else {
                                            savePayrateRequest
                                                .post(data)
                                                .then((res) => {
                                                    actions.resetForm();
                                                    props.onSuccess();
                                                    toast.success($t('Payrate was saved successfully'));
                                                })
                                                .catch((err) => {
                                                    console.log({ err });
                                                    toast.error(err.response?.data?.message || $t('Something went wrong while saving the payrate'));
                                                })
                                                .finally(() => {
                                                    actions.setSubmitting(false);
                                                });
                                        }
                                    }}
                                >
                                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                                        <>
                                            <TextFormField label={$t('Name')} type="text" name="name"></TextFormField>
                                            <TextFormField label={$t('Min')} required={true} type="text" placeholder={$t('Min')} name="min"></TextFormField>
                                            <TextFormField label={$t('Max')} required={true} type="text" placeholder={$t('Max')} name="max"></TextFormField>

                                            <div className="tw-mt-6">
                                                <h3 className="tw-font-bold tw-text-base tw-mb-2">{$t('Percentages by Year')}</h3>
                                                <table className="tw-border-collapse tw-border tw-border-gray-300 tw-w-full">
                                                    <thead>
                                                        <tr className="tw-bg-gray-100">
                                                            <th className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">{$t('Year')}</th>
                                                            <th className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">{$t('Inflation (%)')}</th>
                                                            <th className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">{$t('Bonus (%)')}</th>
                                                            <th className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">
                                                                {$t('Bonus Pay Date')}
                                                                <Tooltip
                                                                    placement="top-end"
                                                                    tooltipContent={`This date will be used to allocate the yearly bonus payment for cashflow purposes`}
                                                                    tooltipType="text"
                                                                    backgroundClass="tw-bg-charcoal tw-z-[9999]"
                                                                >
                                                                    <div className="tw-flex tw-justify-center">
                                                                        <CircleInfoIcon className="tw-w-4 tw-h-4 tw-text-blue" />
                                                                    </div>
                                                                </Tooltip>
                                                            </th>
                                                            <th className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">{$t('On Costs (%)')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.keys(props.defaultPercentage.inflation).map((year, index) => {
                                                            return (
                                                                <tr key={year}>
                                                                    <td className="tw-border tw-border-gray-300 px-4 tw-py-2 tw-text-center">{year}</td>
                                                                    <td className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">
                                                                        {props.defaultPercentage.inflation[year] === null && <span>-</span>}
                                                                        {props.defaultPercentage.inflation[year] !== null && (
                                                                            <TextFormField
                                                                                required={true}
                                                                                type="number"
                                                                                step="0.1"
                                                                                name={`inflation.${year}`}
                                                                                disabled={props.defaultPercentage.inflation[year] === null}
                                                                                hideLabel={true}
                                                                            ></TextFormField>
                                                                        )}
                                                                    </td>
                                                                    <td className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">
                                                                        {props.defaultPercentage.bonus[year] === null && <span>-</span>}
                                                                        {props.defaultPercentage.bonus[year] !== null && (
                                                                            <TextFormField
                                                                                required={true}
                                                                                type="number"
                                                                                step="0.1"
                                                                                name={`bonus.${year}`}
                                                                                disabled={props.defaultPercentage.bonus[year] === null}
                                                                                hideLabel={true}
                                                                            ></TextFormField>
                                                                        )}
                                                                    </td>
                                                                    <td className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">
                                                                        <DatePicker
                                                                            selected={
                                                                                values.bonusPayDate[year] instanceof Date
                                                                                    ? values.bonusPayDate[year]
                                                                                    : values.bonusPayDate[year]
                                                                                      ? new Date(
                                                                                            values.bonusPayDate[year] +
                                                                                                `T${String(12 - new Date().getTimezoneOffset() / 60).padStart(2, '0')}:00:00Z`,
                                                                                        )
                                                                                      : null
                                                                            }
                                                                            onChange={(date) => {
                                                                                if (!date) {
                                                                                    setFieldValue(`bonusPayDate.${year}`, '');
                                                                                    return;
                                                                                }

                                                                                const correctedDate = new Date(
                                                                                    date.getTime() - date.getTimezoneOffset() * 60000,
                                                                                );
                                                                                const formattedDate = correctedDate.toISOString().split('T')[0];

                                                                                setFieldValue(`bonusPayDate.${year}`, formattedDate);
                                                                            }}
                                                                            dateFormat="dd/MM/yyyy"
                                                                            className="tw-border tw-rounded-md tw-p-2 tw-text-center tw-w-40"
                                                                            minDate={index === 0 ? new Date(props.eventStartDate) : new Date(`${year}-01-01`)}
                                                                            maxDate={
                                                                                index === Object.keys(props.defaultPercentage.bonusPayDate).length - 1
                                                                                    ? new Date(props.eventEndDate)
                                                                                    : new Date(`${year}-12-31`)
                                                                            }
                                                                        />

                                                                        {errors.bonusPayDate?.[year] && touched.bonusPayDate?.[year] && (
                                                                            <div className="tw-text-red-500">{errors.bonusPayDate[year]}</div>
                                                                        )}
                                                                    </td>
                                                                    <td className="tw-border tw-border-gray-300 tw-px-4 tw-py-2">
                                                                        <TextFormField
                                                                            required={true}
                                                                            type="number"
                                                                            step="0.1"
                                                                            name={`onCosts.${year}`}
                                                                            hideLabel={true}
                                                                        ></TextFormField>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            <ModalFooter
                                                cancelButtonText={$t('Cancel')}
                                                primaryButtonText={$t('Save')}
                                                onCancel={props.onCancel}
                                                handleSubmit={handleSubmit}
                                                isSubmitting={isSubmitting}
                                            ></ModalFooter>
                                        </>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
